import { useEffect } from 'react'
import { ActivityIndicator } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import { B2, B5, BTN } from '@zet/design-system/typography'
import { useSubmitArn } from '@zet/feature-leads/api/leadDetails/hooks'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { HDFC_ASKCODES } from '@zet/utils/Constants'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import ArnStatus from './arnStatus'

interface ArnTrackApplicationProps {
  item: object
  askCode: string
  ask: object
  brandName: string
}

const schema = z.object({
  referenceId: z
    .string({ required_error: i18n.t('hdfcArn.arnRequired') })
    .min(1, { message: i18n.t('hdfcArn.arnRequired') })
    .regex(/^[a-zA-Z0-9]+$/, {
      message: i18n.t('hdfcArn.arnAlphaNumericErrorMessage'),
    })
    .regex(/^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$/, {
      message: i18n.t('hdfcArn.arnAtleastOneAlphaNumericErrorMessage'),
    })
    .length(16, { message: i18n.t('hdfcArn.arnLengthErrorMessage') }),
})

const ArnTrackApplication: React.FC<ArnTrackApplicationProps> = (props) => {
  const { item, askCode, ask, brandName } = props
  const { navigate } = useNavigation()
  const { data: user } = useUserContext()

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
    setError,
    getValues,
  } = useForm({
    defaultValues: {
      referenceId: item?.referenceId ?? '',
    },
    reValidateMode: 'onChange',
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (item?.referenceId) {
      setValue('referenceId', item?.referenceId ?? '', {
        shouldTouch: true,
      })
    }
  }, [item?.referenceId])

  useEffect(() => {
    if (askCode === HDFC_ASKCODES.$ARN_INCORRECT) {
      setError('referenceId', {})
    }
  }, [askCode])

  const { mutate, isLoading } = useSubmitArn(item?.id ?? '')

  const submitArn = (data) => {
    mutate({
      referenceId: data?.referenceId?.toUpperCase?.(),
      userFormId: item?.id,
    })
  }

  return (
    <Stack className="mx-3">
      <B2 className="mt-3">
        {i18n.t('hdfcArn.inputTitle')}
        <B2 className="text-error-100">*</B2>
      </B2>
      <Controller
        control={control}
        name="referenceId"
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => {
          return (
            <YStack className="mb-2 mt-2">
              <Input
                placeholder={i18n.t('hdfcArn.arnPlaceHolder')}
                className="mt-2"
                style={{
                  borderColor: error
                    ? zetColors.error[100]
                    : zetColors.grays[40],
                }}
                autoFocus
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                autoCapitalize="characters"
                editable={askCode !== HDFC_ASKCODES.$ARN_PENDING}
                ref={ref}
                maxLength={16}
                iconRight={
                  !!error && (
                    <Icon color={zetColors.error[100]} type="ErrorOutlined" />
                  )
                }
              />
              {!!error?.message && (
                <Stack className="mx-1 -mt-3">
                  <B5 className="text-error-100">{error.message}</B5>
                </Stack>
              )}
            </YStack>
          )
        }}
      />
      {askCode === HDFC_ASKCODES.$ARN_PENDING ? (
        <ArnStatus
          askTitle={ask?.title}
          askDescription={ask?.description}
          date={ask?.createdDate}
        />
      ) : (
        <>
          <Button
            fullWidth
            disabled={!isValid || isLoading}
            className="mb-4/2"
            onPress={handleSubmit(submitArn)}
            eventName="click_action"
            eventPayload={{
              item_name: 'lead_page_arn_track_application',
              page_name: askCode,
              partner_id: item?.partnerId,
              brand_name: brandName,
              lead_id: item?.id,
              user_id: user.id,
              customer_id: item?.customerId,
              arn: getValues('referenceId'),
            }}
          >
            <XStack className="items-center justify-center">
              {isLoading ? (
                <ActivityIndicator size={'small'} color="white" />
              ) : (
                <BTN className="text-white">{i18n.t('hdfcArn.track')}</BTN>
              )}
            </XStack>
          </Button>
          <Button
            fullWidth
            outlined
            className="mb-4/2 items-center"
            onPress={() =>
              navigate('ShareCardLink', {
                brandId: item?.partnerId,
                customerName: item?.name,
                customerId: item?.customerId,
                reopen: false,
                routeName: 'forCustomerFlow',
                eventName: 'click_action',
                eventPayload: {
                  item_name: 'lead_page_share_message_preview_screen_share',
                  lead_id: item?.id,
                  page_name: askCode,
                  user_id: user?.id,
                  customer_id: item?.customerId,
                  partner_id: item?.partnerId,
                  brand_name: brandName,
                },
              })
            }
            eventName="click_action"
            eventPayload={{
              item_name: 'lead_page_arn_share_application',
              page_name: askCode,
              partner_id: item?.partnerId,
              brand_name: brandName,
              lead_id: item?.id,
              user_id: user?.id,
              customer_id: item?.customerId,
              arn: getValues('referenceId'),
            }}
          >
            <XStack>
              <Icon type="Share" size={24} color={zetColors.primary[100]} />
              <BTN className="text-primary-100">{i18n.t('hdfcArn.share')}</BTN>
            </XStack>
          </Button>
        </>
      )}
    </Stack>
  )
}

export default ArnTrackApplication
